// The purchase request form (P1.2, 01-SPEC §3 step 1), described once: the broker validates with
// these schemas and the screen uses the same limits and the same submit check. No prices: the
// requester gives an optional estimated budget only.

import { MAX_UPLOAD_BYTES } from '../../shared/files';
import { z } from 'zod';

export const LINE_TYPES = ['one-off', 'recurring', 'service'] as const;
export type LineType = (typeof LINE_TYPES)[number];

export const UNITS = [
  'piece',
  'box',
  'pack',
  'set',
  'kg',
  'litre',
  'metre',
  'hour',
  'day',
  'month',
  'licence',
  'lot',
] as const;
export type Unit = (typeof UNITS)[number];

export const MAX_LINES = 50;
export const MAX_FILES = 10;
export const MAX_FILE_BYTES = MAX_UPLOAD_BYTES;
/** Estimated budget, in major units (SAR, AED, EGP). Stored as integer minor units. */
export const MAX_BUDGET = 100_000_000;

const DATE = /^\d{4}-\d{2}-\d{2}$/;

// Free text is stored as plain text and shown escaped; angle brackets are refused so no markup
// ever reaches a document template (03-SECURITY "No HTML in free text").
const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .regex(/^[^<>]*$/, 'Remove < and >');

const decimals = (v: number, places: number) => {
  const scaled = v * 10 ** places;
  return Math.abs(scaled - Math.round(scaled)) < 1e-6;
};

export const lineSchema = z.strictObject({
  description: text(500).min(1, 'Required'),
  specs: text(1000).optional(),
  qty: z
    .number()
    .positive('More than 0')
    .max(1_000_000)
    .refine((v) => decimals(v, 3), 'Up to 3 decimals'),
  unit: z.enum(UNITS),
  lineType: z.enum(LINE_TYPES),
});
export type PrLine = z.infer<typeof lineSchema>;

/**
 * What the browser sends for a draft or a submit. Never entity, office or currency. `chargedTo` is
 * the cost centre (vertical) the requester picks (D105); the broker checks it against
 * VerticalEntityMap and derives the entity from it. Left out, it is the requester's own vertical.
 */
export const prInputSchema = z.strictObject({
  title: text(200).min(1, 'Required'),
  chargedTo: text(80).optional(),
  category: z.union([z.literal(''), z.string().regex(/^[A-Z0-9][A-Z0-9-]{1,29}$/)]).optional(),
  location: text(80).optional(),
  neededBy: z.union([z.literal(''), z.string().regex(DATE, 'Use a date')]).optional(),
  justification: text(2000).optional(),
  estBudget: z
    .number()
    .nonnegative()
    .max(MAX_BUDGET)
    .refine((v) => decimals(v, 2), 'Up to 2 decimals')
    .nullable()
    .optional(),
  rush: z.boolean().optional(),
  rushReason: text(1000).optional(),
  lines: z.array(lineSchema).max(MAX_LINES).optional(),
});
export type PrInput = z.infer<typeof prInputSchema>;

/** Fields a draft may leave out but a submit needs. Empty object = ready to submit. */
export function missingForSubmit(input: PrInput): Record<string, string> {
  const out: Record<string, string> = {};
  if (!input.category) out.category = 'Required';
  if (!input.location) out.location = 'Required';
  if (!input.justification) out.justification = 'Required';
  if (!input.lines?.length) out.lines = 'Add at least one item';
  if (input.rush && !input.rushReason) out.rushReason = 'Required for a rush request';
  return out;
}

/** Major units to integer minor units (halalas, fils, piastres: all three use 2 decimals). */
export function toMinor(major: number): number {
  return Math.round(major * 100);
}

/** Today's date (YYYY-MM-DD) in the office's time zone. */
export function todayIn(timeZone: string, at: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone }).format(at);
}

/** A needed-by date must be a real calendar date, today or later in the office's time zone. */
export function neededByProblem(value: string, timeZone: string, at?: Date): string | null {
  const d = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== value) return 'Use a date';
  return value < todayIn(timeZone, at) ? 'Must be today or later' : null;
}
